import React from "react";
import { motion } from "framer-motion";
import { Zap, PenLine, GitBranch, Code2, Shield, User } from "lucide-react";

const features = [
  { icon: Zap,       title: "Fast Delivery",         meta: "7–14 days",         description: "Most automations are live within two weeks. No lengthy onboarding, no unnecessary meetings.", color: "blue" },
  { icon: PenLine,   title: "Custom AI Solutions",   meta: "100% bespoke",      description: "No off-the-shelf templates. Every system is built specifically for your business logic.",    color: "violet" },
  { icon: GitBranch, title: "Scalable Systems",      meta: "Built to grow",     description: "Start small, scale to thousands of interactions without rebuilding anything from scratch.",   color: "blue" },
  { icon: Code2,     title: "Modern Tech Stack",     meta: "n8n · Make · GPT",  description: "We use cutting-edge tools — OpenAI, n8n, Retell, and custom APIs — not outdated platforms.", color: "violet" },
  { icon: Shield,    title: "Reliable Automation",   meta: "24/7 uptime",       description: "Error handling, failover logic, and real-time monitoring built into every system we deliver.", color: "blue" },
  { icon: User,      title: "Founder-Led Attention", meta: "Direct access",     description: "You work directly with the builder. No account managers, no delegation. Pure expert execution.", color: "violet" },
];

const onMove = (e) => {
  const rect = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty("--mouse-x", `${e.clientX - rect.left}px`);
  e.currentTarget.style.setProperty("--mouse-y", `${e.clientY - rect.top}px`);
};

export default function WhyChooseUs() {
  return (
    <section data-testid="why-choose-section" className="py-24 md:py-36 relative">
      <div className="section-divider" />

      <div className="absolute top-0 right-0 w-[700px] h-[500px] pointer-events-none" style={{ background: "radial-gradient(ellipse at 100% 0%, rgba(59,130,246,0.05) 0%, transparent 60%)" }} />

      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-12">
        {/* Header — asymmetric layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-end mb-16">
          <div>
            <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="overline-badge mb-4">
              Why Choose Us
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="text-[2.2rem] sm:text-[2.8rem] lg:text-[3.2rem] font-black tracking-[-0.04em] text-white leading-[1.0]"
            >
              Built different.<br />
              <span className="gradient-text-static">Delivers results.</span>
            </motion.h2>
          </div>
          <motion.p
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
            transition={{ delay: 0.15 }}
            className="text-[15px] text-white/38 leading-[1.75] max-w-md"
          >
            We're not an agency that outsources your project. We're a focused AI automation studio that builds, tests, and optimizes every system ourselves — with obsessive attention to quality.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {features.map((f, i) => {
            const Icon = f.icon;
            const isBlue = f.color === "blue";
            return (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                onMouseMove={onMove}
                className="spotlight-card gradient-border-card card-inner-top group p-6 rounded-2xl border border-white/[0.06] bg-[#0E0E12] hover:border-white/[0.1] hover:-translate-y-1 transition-all duration-300 cursor-default"
              >
                <div className="flex items-start justify-between mb-5">
                  {/* Icon */}
                  <div className="relative">
                    <div className={`absolute inset-0 rounded-full blur-md opacity-0 group-hover:opacity-100 transition-opacity scale-150 ${isBlue ? "bg-blue-500/20" : "bg-violet-500/20"}`} />
                    <div className={`relative w-10 h-10 rounded-xl flex items-center justify-center border transition-all duration-300 ${
                      isBlue
                        ? "bg-blue-500/[0.08] border-blue-500/20 group-hover:border-blue-500/40"
                        : "bg-violet-500/[0.08] border-violet-500/20 group-hover:border-violet-500/40"
                    }`}>
                      <Icon className={`w-4.5 h-4.5 ${isBlue ? "text-blue-400/80" : "text-violet-400/80"}`} style={{ width: "18px", height: "18px" }} strokeWidth={1.5} />
                    </div>
                  </div>
                  {/* Meta label */}
                  <span className="text-[10px] font-mono text-white/20 mt-1">{f.meta}</span>
                </div>
                <h3 className="text-[14px] font-semibold text-white/85 mb-2 tracking-[-0.01em]">{f.title}</h3>
                <p className="text-[12.5px] text-white/35 leading-[1.65]">{f.description}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
